<script setup>
/* eslint-disable no-console */
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useAccount } from 'dashboard/composables/useAccount';
import AccountVoicesAPI from 'dashboard/api/accountVoices';
import VoiceStudioAPI from 'dashboard/api/voiceStudio';
import TtsAPI, { previewErrorMessage } from 'dashboard/api/tts';

const props = defineProps({
  modelValue: {
    type: String,
    default: 'nova',
  },
  // TTS provider da voz selecionada: 'openai' (vozes padrão) ou 'elevenlabs'
  // (vozes clonadas da conta). O seletor emite os dois juntos.
  provider: {
    type: String,
    default: 'openai',
  },
});

const emit = defineEmits(['update:modelValue', 'update:provider']);
const { t } = useI18n();
const { accountScopedRoute } = useAccount();
const voicesRoute = computed(() => accountScopedRoute('voices_list'));

const playingVoice = ref(null);
const audioRef = ref(null);
const isLoading = ref(false);
const errorMessage = ref(null);

// Vozes clonadas da conta (ElevenLabs). Só as prontas entram no seletor —
// as em processamento/falha ficam só na tela de gerenciamento (Configurações › Vozes).
const clonedVoices = ref([]);

const fetchClonedVoices = async () => {
  try {
    const { data } = await AccountVoicesAPI.get();
    clonedVoices.value = (data?.payload || []).filter(
      voice => voice.status === 'ready' && voice.voice_id
    );
  } catch {
    // Vozes clonadas são opcionais; falha silenciosa mantém só as vozes padrão.
    clonedVoices.value = [];
  }
};

// Vozes PRONTAS do Voice Studio: 1.126 arquétipos que rodam na NOSSA GPU, então
// não custam por caractere como as da OpenAI. A amostra vem da galeria com cache
// do runtime — tocar é instantâneo, não sintetiza na hora.
const PAGE_SIZE = 24;
const studioVoices = ref([]);
const studioTotal = ref(0);
const studioLoading = ref(false);
const studioSearch = ref('');
const studioGender = ref('');

const hasMoreStudio = computed(
  () => studioVoices.value.length < studioTotal.value
);

const fetchStudioVoices = async ({ append = false } = {}) => {
  studioLoading.value = true;
  try {
    const { data } = await VoiceStudioAPI.list({
      q: studioSearch.value || undefined,
      gender: studioGender.value || undefined,
      limit: PAGE_SIZE,
      offset: append ? studioVoices.value.length : 0,
    });
    const recebidas = data?.voices || [];
    studioVoices.value = append
      ? [...studioVoices.value, ...recebidas]
      : recebidas;
    studioTotal.value = data?.total || 0;
  } catch {
    // Catálogo é opcional: se o runtime não responder, a tela segue com as demais.
    if (!append) {
      studioVoices.value = [];
      studioTotal.value = 0;
    }
  } finally {
    studioLoading.value = false;
  }
};

let buscaTimer = null;
const onStudioSearch = () => {
  clearTimeout(buscaTimer);
  buscaTimer = setTimeout(() => fetchStudioVoices(), 350);
};

const setStudioGender = genero => {
  studioGender.value = studioGender.value === genero ? '' : genero;
  fetchStudioVoices();
};

// A amostra vem por blob, não por <audio src>: o navegador não manda os
// cabeçalhos de auth da API numa requisição de mídia, e o endpoint responde 401.
const playStudioPreview = async voiceId => {
  if (playingVoice.value === voiceId) {
    audioRef.value?.pause();
    audioRef.value = null;
    playingVoice.value = null;
    return;
  }
  audioRef.value?.pause();
  playingVoice.value = voiceId;
  isLoading.value = true;
  try {
    const { data } = await VoiceStudioAPI.preview(voiceId);
    const url = URL.createObjectURL(data);
    audioRef.value = new Audio(url);
    const limpar = () => {
      playingVoice.value = null;
      URL.revokeObjectURL(url);
    };
    audioRef.value.onended = limpar;
    audioRef.value.onerror = limpar;
    audioRef.value.play();
  } catch {
    playingVoice.value = null;
    useAlert(t('AI_AGENTS.TTS.ERROR_VOICE_PREVIEW'));
  } finally {
    isLoading.value = false;
  }
};

// Escolher: o arquétipo vira perfil no runtime e o id do PERFIL é o que fica
// gravado no agente (o id do arquétipo não serve para sintetizar). O runtime é
// idempotente, então dá pra guardar arquétipo→perfil e marcar o card escolhido.
const studioProfileByArchetype = ref({});

const selectStudioVoice = async voice => {
  studioLoading.value = true;
  try {
    const { data } = await VoiceStudioAPI.use(voice.id, voice.name);
    if (data?.voice_id) {
      studioProfileByArchetype.value[voice.id] = data.voice_id;
      emit('update:modelValue', data.voice_id);
      emit('update:provider', 'navivoice');
    }
  } catch {
    useAlert(t('AI_AGENTS.TTS.ERROR_VOICE_PREVIEW'));
  } finally {
    studioLoading.value = false;
  }
};

const isStudioSelected = voice =>
  props.provider === 'navivoice' &&
  studioProfileByArchetype.value[voice.id] === props.modelValue;

const isSelected = (voiceId, voiceProvider) =>
  props.modelValue === voiceId &&
  (props.provider || 'openai') === voiceProvider;
const isPlaying = voiceId => playingVoice.value === voiceId;

onMounted(() => {
  fetchClonedVoices();
  fetchStudioVoices();
});

const generatePreview = async (voiceId, voiceProvider) => {
  if (playingVoice.value === voiceId) {
    if (audioRef.value) {
      audioRef.value.pause();
      audioRef.value = null;
    }
    playingVoice.value = null;
    return;
  }

  playingVoice.value = voiceId;
  isLoading.value = true;
  errorMessage.value = null;

  try {
    const { data: blob } = await TtsAPI.preview({
      voice: voiceId,
      provider: voiceProvider,
      text: t('AI_AGENTS.TTS.PREVIEW_TEXT'),
    });
    const url = URL.createObjectURL(blob);

    if (audioRef.value) {
      audioRef.value.pause();
    }

    audioRef.value = new Audio(url);
    audioRef.value.onended = () => {
      playingVoice.value = null;
      URL.revokeObjectURL(url);
    };
    audioRef.value.onerror = () => {
      playingVoice.value = null;
      URL.revokeObjectURL(url);
    };
    audioRef.value.play();
  } catch (error) {
    // Com resposta = o servidor recusou (login, voz, limite): mostra o motivo.
    // Sem resposta = falha de rede.
    if (error?.response) {
      const msg =
        (await previewErrorMessage(error)) ||
        t('AI_AGENTS.TTS.ERROR_VOICE_PREVIEW');
      errorMessage.value = msg;
      useAlert(msg);
    } else {
      errorMessage.value = t('AI_AGENTS.TTS.ERROR_CONNECTION');
      useAlert(t('AI_AGENTS.TTS.ERROR_TTS_SERVER'));
    }
    playingVoice.value = null;
  } finally {
    isLoading.value = false;
  }
};

// Selecionar uma voz seta voz + provider juntos, pra o agente rotear o TTS
// pro provider certo (OpenAI padrão ou ElevenLabs da voz clonada).
const selectVoice = (voiceId, voiceProvider) => {
  emit('update:modelValue', voiceId);
  emit('update:provider', voiceProvider);
};
</script>

<template>
  <div class="space-y-4">
    <!-- Vozes prontas do Voice Studio (nossa GPU, sem custo por caractere) -->
    <div class="space-y-2">
      <p class="text-xs font-medium text-n-slate-11 flex items-center gap-1.5">
        <span class="i-lucide-sparkles w-3.5 h-3.5" />
        {{ t('AI_AGENTS.TTS.STUDIO_VOICES') }}
        <span v-if="studioTotal" class="text-n-slate-10">
          ({{ studioTotal }})
        </span>
      </p>

      <div class="flex gap-2">
        <input
          v-model="studioSearch"
          type="search"
          :placeholder="t('AI_AGENTS.TTS.STUDIO_SEARCH')"
          class="flex-1 h-8 px-2.5 text-xs rounded-lg bg-n-solid-3 border border-n-weak text-n-slate-12 placeholder:text-n-slate-10"
          @input="onStudioSearch"
        />
        <button
          v-for="g in ['male', 'female']"
          :key="g"
          type="button"
          class="h-8 px-2.5 text-xs rounded-lg border transition-colors"
          :class="
            studioGender === g
              ? 'border-n-brand bg-n-brand/10 text-n-slate-12'
              : 'border-n-weak bg-n-solid-3 text-n-slate-11 hover:border-n-slate-8'
          "
          @click="setStudioGender(g)"
        >
          {{ t(`AI_AGENTS.TTS.GENDER_${g.toUpperCase()}`) }}
        </button>
      </div>

      <div v-if="studioVoices.length" class="grid grid-cols-2 gap-2">
        <button
          v-for="voice in studioVoices"
          :key="voice.id"
          type="button"
          class="relative flex items-center gap-2 p-2.5 rounded-xl border-2 transition-all text-left"
          :class="[
            isStudioSelected(voice)
              ? 'border-n-brand bg-n-brand/10'
              : 'border-n-weak hover:border-n-slate-8 bg-n-solid-3',
          ]"
          @click="selectStudioVoice(voice)"
        >
          <div
            class="w-8 h-8 rounded-full flex items-center justify-center text-white shrink-0"
            :class="voice.gender === 'male' ? 'bg-blue-600' : 'bg-fuchsia-600'"
          >
            <span class="i-lucide-user w-4 h-4" />
          </div>
          <div class="flex-1 min-w-0">
            <p class="text-sm font-medium text-n-slate-12 truncate">
              {{ voice.name }}
            </p>
            <p class="text-[10px] text-n-slate-11 truncate">
              {{ [voice.age, voice.pitch].filter(Boolean).join(' · ') }}
            </p>
          </div>
          <button
            type="button"
            class="w-7 h-7 rounded-full flex items-center justify-center transition-colors shrink-0"
            :class="
              isPlaying(voice.id)
                ? 'bg-n-brand text-white'
                : 'bg-n-alpha-3 text-n-slate-11 hover:bg-n-alpha-4 hover:text-n-slate-12'
            "
            @click.stop="playStudioPreview(voice.id)"
          >
            <span
              v-if="isPlaying(voice.id)"
              class="i-lucide-pause w-3.5 h-3.5"
            />
            <span v-else class="i-lucide-play w-3.5 h-3.5" />
          </button>
          <div
            v-if="isStudioSelected(voice)"
            class="absolute top-1.5 right-1.5"
          >
            <span class="i-lucide-check-circle w-3.5 h-3.5 text-n-brand" />
          </div>
        </button>
      </div>

      <p v-else-if="!studioLoading" class="text-xs text-n-slate-11 py-2">
        {{ t('AI_AGENTS.TTS.STUDIO_EMPTY') }}
      </p>

      <button
        v-if="hasMoreStudio"
        type="button"
        class="w-full h-8 text-xs rounded-lg border border-n-weak bg-n-solid-3 text-n-slate-11 hover:border-n-slate-8 disabled:opacity-50"
        :disabled="studioLoading"
        @click="fetchStudioVoices({ append: true })"
      >
        {{
          studioLoading
            ? t('AI_AGENTS.TTS.LOADING')
            : t('AI_AGENTS.TTS.STUDIO_MORE')
        }}
      </button>
    </div>

    <!-- Vozes clonadas da conta (ElevenLabs ou Voice Studio) -->
    <div class="space-y-2">
      <p
        class="text-xs font-medium text-n-slate-11 flex items-center gap-1.5 pt-1"
      >
        <span class="i-lucide-mic w-3.5 h-3.5" />
        {{ t('AI_AGENTS.TTS.CUSTOM_VOICES') }}
      </p>
      <div v-if="clonedVoices.length" class="grid grid-cols-2 gap-2">
        <button
          v-for="voice in clonedVoices"
          :key="voice.voice_id"
          type="button"
          class="relative flex items-center gap-2 p-2.5 rounded-xl border-2 transition-all text-left"
          :class="[
            isSelected(voice.voice_id, voice.provider || 'elevenlabs')
              ? 'border-n-brand bg-n-brand/10'
              : 'border-n-weak hover:border-n-slate-8 bg-n-solid-3',
          ]"
          @click="selectVoice(voice.voice_id, voice.provider || 'elevenlabs')"
        >
          <div
            class="w-8 h-8 rounded-full flex items-center justify-center text-white shrink-0 bg-n-brand"
          >
            <span class="i-lucide-sparkles w-4 h-4" />
          </div>

          <div class="flex-1 min-w-0">
            <p class="text-sm font-medium text-n-slate-12 truncate">
              {{ voice.name }}
            </p>
            <p class="text-[10px] text-n-slate-11 truncate">
              {{
                voice.provider === 'navivoice'
                  ? t('AI_AGENTS.TTS.PROVIDERS.NAVIVOICE')
                  : t('AI_AGENTS.TTS.PROVIDERS.ELEVENLABS')
              }}
            </p>
          </div>

          <button
            type="button"
            class="w-7 h-7 rounded-full flex items-center justify-center transition-colors shrink-0"
            :class="
              isPlaying(voice.voice_id)
                ? 'bg-n-brand text-white'
                : 'bg-n-alpha-3 text-n-slate-11 hover:bg-n-alpha-4 hover:text-n-slate-12'
            "
            @click.stop="
              generatePreview(voice.voice_id, voice.provider || 'elevenlabs')
            "
          >
            <span
              v-if="playingVoice === voice.voice_id && isLoading"
              class="i-lucide-loader-2 w-3.5 h-3.5 animate-spin"
            />
            <span
              v-else-if="isPlaying(voice.voice_id)"
              class="i-lucide-pause w-3.5 h-3.5"
            />
            <span v-else class="i-lucide-play w-3.5 h-3.5" />
          </button>

          <div
            v-if="isSelected(voice.voice_id, voice.provider || 'elevenlabs')"
            class="absolute top-1.5 right-1.5"
          >
            <span class="i-lucide-check-circle w-3.5 h-3.5 text-n-brand" />
          </div>
        </button>
      </div>

      <!-- Sem clones ainda: atalho pra clonar (leva pra Configurações › Vozes) -->
      <router-link
        v-else
        :to="voicesRoute"
        class="flex items-center gap-2 rounded-xl border border-dashed border-n-weak px-3 py-2.5 text-sm text-n-slate-11 transition-colors hover:border-n-brand hover:text-n-slate-12"
      >
        <span class="i-lucide-plus size-4 shrink-0" />
        <span class="flex-1">{{ t('AI_AGENTS.TTS.CLONE_CTA') }}</span>
        <span class="i-lucide-arrow-right size-3.5 shrink-0" />
      </router-link>
    </div>

    <p class="text-xs text-n-slate-10">
      {{ t('AI_AGENTS.TTS.PREVIEW_HINT') }}
    </p>
    <p class="text-xs text-n-slate-10 mt-1">
      {{ t('AI_AGENTS.TTS.PORTUGUESE_SUPPORT') }}
    </p>
  </div>
</template>
